'use client';
import { factory, Box, Grid, GridColumn, Flex, BrandLogo, Link, omitText, Typography, CartBadge } from '@kohler-dsm-library/kohler-core';
import { ArrowDownIcon, SearchIcon, MenuIcon } from '@kohler-dsm-library/kohler-icons';
import clsx from 'clsx';
import React, { useState, useRef } from 'react';
import { NavigationItem } from '../NavigationItem/NavigationItem.mjs';
import { OverlayMenu } from '../OverlayMenu/OverlayMenu.mjs';
import useResponsiveNav from './MainNavigationBar.hooks.mjs';
import classes from './MainNavigationBar.module.css.mjs';
import { mainNavigationBarVariantClasses } from './MainNavigationBar.utils.mjs';

const MainNavigationBar = factory((props, ref) => {
  const {
    brandCTA,
    menuItems,
    showShadow,
    showDesktopActions = true,
    searchCTA,
    menuCTA,
    searchText = "Search",
    buttonCTA,
    variant = "universal",
    utilityLinks,
    showBackground = false,
    mod,
    openMobileMenu,
    closeCTA,
    setIsOpenMobileMenu,
    isStatic,
    style,
    showMenuDelay,
    transitionDuration,
    isAlwaysSticky,
    ...rest
  } = props;
  const isUniversal = variant === "universal";
  const { brand: brandType, ...brandCTAProps } = brandCTA;
  const [activeMenu, setActiveMenu] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const { visibleItems, overflowItems, isMoreOpen, setIsMoreOpen, navRef, containerRef, moreButtonRef } = useResponsiveNav(menuItems);
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      className: clsx(
        classes.mainNav,
        showShadow && classes.mainNavShadow,
        mainNavigationBarVariantClasses[variant],
        showBackground && classes.mainNavBackground,
        (isStatic || showBackground) && !isAlwaysSticky && classes.mainNavRelative,
        "kohler-main-navigation-bar-root"
      ),
      ref,
      style: {
        "--transition-overlay-duration": typeof transitionDuration === "number" ? `${transitionDuration}ms` : void 0,
        ...style
      },
      mod,
      ...rest
    },
    /* @__PURE__ */ React.createElement(Grid, null, /* @__PURE__ */ React.createElement(GridColumn, null, /* @__PURE__ */ React.createElement(Flex, { className: isUniversal ? classes.mainNavWrapperUniversal : classes.mainNavWrapperDefault }, /* @__PURE__ */ React.createElement(Flex, { className: clsx(isUniversal ? classes.menuItemsUniversal : classes.menuItemsVTwo, classes.menuItems) }, /* @__PURE__ */ React.createElement(Box, { component: "a", className: classes.brandLogo, "aria-label": `${brandType} logo`, ...brandCTAProps }, /* @__PURE__ */ React.createElement(BrandLogo, { brand: brandType })), menuItems && /* @__PURE__ */ React.createElement(Box, { ref: containerRef, className: classes.menuLinksWrapper, component: "nav" }, /* @__PURE__ */ React.createElement(
      Flex,
      {
        className: clsx(isUniversal ? classes.menuLinksUniversal : classes.menuLinksVTwo, classes.menuLinks),
        component: "ul",
        ref: navRef,
        role: "menubar"
      },
      visibleItems.map((navItem) => /* @__PURE__ */ React.createElement(
        Box,
        {
          component: "li",
          key: navItem.text,
          id: navItem.text || `nav-item-${navItem.text}`,
          className: clsx(classes.navigationItemWrapper),
          onMouseEnter: () => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = setTimeout(
              () => {
                setActiveMenu(navItem.text);
              },
              typeof showMenuDelay === "number" ? showMenuDelay : 0
            );
          },
          onMouseLeave: () => {
            if (hoverTimeoutRef.current) {
              clearTimeout(hoverTimeoutRef.current);
              hoverTimeoutRef.current = null;
            }
            setActiveMenu(null);
          },
          tabIndex: 0,
          role: "menuitem",
          "aria-haspopup": !!navItem.CTALists,
          "aria-expanded": activeMenu === navItem.text,
          onKeyDown: (e) => {
            if ((e.key === "Enter" || e.key === " ") && navItem.CTALists) {
              e.preventDefault();
              const navText = navItem.text;
              setActiveMenu((prev) => prev === navText ? null : navText);
            }
          }
        },
        /* @__PURE__ */ React.createElement(
          NavigationItem,
          {
            text: navItem.text,
            isSelected: navItem.isSelected,
            href: navItem.href,
            variant,
            className: clsx(
              isUniversal ? classes.navItemUniversal : classes.navItemVTwo,
              classes.navItem,
              activeMenu === navItem.text && isUniversal && classes.navItemUnderline,
              activeMenu === navItem.text && !isUniversal && classes.navItemUnderlineV2
            )
          }
        ),
        navItem.CTALists && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
          Box,
          {
            className: clsx(
              classes.navigationOverlayMenuWrapper,
              activeMenu === navItem.text ? classes.navigationOverlayMenuVisible : classes.navigationOverlayMenuHidden
            )
          },
          /* @__PURE__ */ React.createElement(OverlayMenu, { mod: "white", ...navItem })
        ))
      )),
      overflowItems.length > 0 && /* @__PURE__ */ React.createElement(
        Box,
        {
          component: "li",
          ref: moreButtonRef,
          className: clsx(classes.navigationItemWrapper, classes.moreButton),
          onMouseEnter: () => setIsMoreOpen(true),
          onMouseLeave: () => setIsMoreOpen(false),
          role: "menuitem"
        },
        /* @__PURE__ */ React.createElement(
          NavigationItem,
          {
            className: clsx(isUniversal ? classes.navItemUniversal : classes.navItemVTwo, classes.navItem),
            text: /* @__PURE__ */ React.createElement(Flex, { gap: 5, align: "center" }, "More ", /* @__PURE__ */ React.createElement(ArrowDownIcon, { size: "xSmall", withBoundingBox: false, style: { flexShrink: 0 } })),
            variant
          }
        ),
        /* @__PURE__ */ React.createElement(
          Box,
          {
            className: clsx(
              classes.navigationOverlayMenuWrapper,
              isMoreOpen ? classes.navigationOverlayMenuVisible : classes.navigationOverlayMenuHidden
            )
          },
          /* @__PURE__ */ React.createElement(OverlayMenu, { mod: "white", isMoreOverlay: true, mainMenuItems: overflowItems })
        )
      ),
      overflowItems.map((navItem) => /* @__PURE__ */ React.createElement(
        Box,
        {
          component: "li",
          key: navItem.text,
          id: navItem.text || `nav-item-${navItem.text}`,
          className: clsx(classes.navigationItemWrapper),
          style: { visibility: "hidden", opacity: 0 }
        },
        /* @__PURE__ */ React.createElement(
          NavigationItem,
          {
            text: navItem.text,
            isSelected: navItem.isSelected,
            href: navItem.href,
            variant,
            className: clsx(
              isUniversal ? classes.navItemUniversal : classes.navItemVTwo,
              classes.navItem,
              activeMenu === navItem.text && isUniversal && classes.navItemUnderline,
              activeMenu === navItem.text && !isUniversal && classes.navItemUnderlineV2
            )
          }
        )
      ))
    ))), (showDesktopActions || buttonCTA) && /* @__PURE__ */ React.createElement(Flex, { className: clsx(classes.utilities, isUniversal ? classes.utilitiesUniversalGap : classes.utilitiesDefaultGap) }, buttonCTA && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Link,
      {
        type: buttonCTA.onClick ? "button" : void 0,
        component: "href" in buttonCTA && buttonCTA.href ? "a" : "button",
        size: "small",
        ...omitText(buttonCTA),
        className: clsx(classes.buttonCTA, buttonCTA.className)
      },
      buttonCTA.text
    )), searchCTA && /* @__PURE__ */ React.createElement(
      Box,
      {
        component: "button",
        type: "button",
        "aria-label": searchText,
        ...searchCTA,
        className: clsx(classes.utilitiesSearch, searchCTA.className)
      },
      /* @__PURE__ */ React.createElement(Typography, { size: "paragraph-small", component: "span" }, searchText),
      /* @__PURE__ */ React.createElement(SearchIcon, { className: classes.utilitiesSearchIcon, withBoundingBox: false })
    ), /* @__PURE__ */ React.createElement(Flex, { className: classes.utilitiesMobile, gap: 15 }, /* @__PURE__ */ React.createElement(
      Box,
      {
        component: "button",
        type: "button",
        "aria-label": searchText,
        ...searchCTA,
        className: clsx(classes.utilitiesSearchMobile, searchCTA?.className)
      },
      /* @__PURE__ */ React.createElement(SearchIcon, { className: classes.utilitiesSearchIcon, withBoundingBox: false })
    ), utilityLinks?.cartBadgeCTA && /* @__PURE__ */ React.createElement(
      CartBadge,
      {
        component: "a",
        "aria-label": "see shopping cart",
        ...utilityLinks?.cartBadgeCTA,
        className: clsx(classes.utilityLink, utilityLinks.className)
      }
    ), /* @__PURE__ */ React.createElement(
      Box,
      {
        component: "button",
        type: "button",
        "aria-label": "open menu",
        ...menuCTA,
        className: clsx(isUniversal ? classes.menuIconUniversal : classes.menuIconVTwo, menuCTA?.className),
        onClick: (e) => {
          if (setIsOpenMobileMenu) {
            setIsOpenMobileMenu(true);
          }
          if (menuCTA?.onClick) {
            menuCTA.onClick(e);
          }
        }
      },
      /* @__PURE__ */ React.createElement(MenuIcon, { size: "medium", withBoundingBox: false })
    ))), openMobileMenu && /* @__PURE__ */ React.createElement(
      OverlayMenu,
      {
        mainMenuItems: menuItems,
        className: classes.mobileOverlayMenu,
        setIsOpenMobileMenu,
        utilityLinks,
        buttonCTA,
        closeCTA
      }
    ))))
  );
});

export { MainNavigationBar };
